import {
	ResponseParams,
	requestHttpGet,
	requestHttpPost,
	requestHttpPut,
	requestHttpDelete,
	ApiError,
} from './RequestBase';

// PayPalクライアントID取得
type GetPayPalClientIdResData = {
	paypal_client_id: string;
};
export const callGetPayPalClientIdApi = async (): Promise<ResponseParams<GetPayPalClientIdResData>> => {
	const result: ResponseParams<GetPayPalClientIdResData> = await requestHttpGet(`/paypal/identity`);
	return result;
};

export type GetSiteSettlementResData = {
	site_cd: string;
	merchant_id: string;
	logo_file_name: string;
	error_button_display: string;
	error_return_url: string;
	payment_specify_list: {
		paypal_display_flg: number;
		credit_card_display_flg: number;
		pay_after_display_flg: number;
	};
};
// サービスサイト決済設定情報取得
export const callGetSiteSettlementApi = async (siteCd: string): Promise<ResponseParams<GetSiteSettlementResData>> => {
	const result: ResponseParams<GetSiteSettlementResData> = await requestHttpGet(`/sites/settlement/${siteCd}`);
	return result;
};

// 注文情報認証
export type GetOrderResData = {
	gid_order_no: string; // 共通決済注文番号
	periodic_payment_id?: string; // 定期購読ID
	paypal_client_token: string; // クライアントトークン
	net_amount: number; // 商品金額
	discount: number; // 値引額
	postage: number; // 送料
	tax: number; // 消費税
	capture_amount: number; // 決済金額
	remarks: string; // 備考
	logo: string; // ロゴ
	merchant_id: string; // マーチャントID
	payment_tokens?: PaymentToken[]; // 保存済みカード情報
	agreement_id?: string; // 保存済みPayPalID
	payment_specify_list?: {
		paypal_hidden_flg?: number;
		credit_hidden_flg?: number;
		deferred_hidden_flg?: number;
	}; // 非表示支払方法
	order_type: number; // 注文区分 1:都度購入, 2:定期購読
	user_type: number; // 注文者区分 1:GakkenID, 2:ゲスト
};
export type PaymentToken = {
	payment_id: string; // 保存済みカード情報ID
	expiry: string; // 有効期限
	brand: string; // カードブランド
	last_digits: string; // カード番号下4桁
};
export const callGetOrderApi = async (gidOrderToken: string): Promise<ResponseParams<GetOrderResData>> => {
	const result: ResponseParams<GetOrderResData> = await requestHttpGet(`/order/token/${gidOrderToken}`);
	return result;
};

// 決済情報作成
export type CreateOrderReqData = {
	gid_order_no: string; // 注文ID
	payment_tokens_id?: string; // 保存済みカード情報ID
	agreement_id?: string;
	paypal_client_metadata_id?: string; // FraudNetのFパラメータ
	payment_type: number; // 支払区分 1:PayPal, 2:クレジットカード
};
export type CreateOrderResData = {
	paypal_order_id: string; // 決済ID
};
export const callCreateOrderApi = async (body: CreateOrderReqData): Promise<ResponseParams<CreateOrderResData>> => {
	const result: ResponseParams<CreateOrderResData> = await requestHttpPost(`/order/checkout`, body);
	return result;
};

// 定期購入用トークン取得
export type GenerateBillingAgreementTokenResData = {
	token_id: string;
};
export const callGenerateBillingAgreementTokenApi = async (): Promise<
	ResponseParams<GenerateBillingAgreementTokenResData>
> => {
	const result: ResponseParams<GenerateBillingAgreementTokenResData> = await requestHttpPost(
		`/paypal/billing-agreement-token`,
		{},
	);
	return result;
};

// 定期購入用承認ID取得
type GenerateBillingAgreementIdReqData = {
	token_id: string;
};
export type GenerateBillingAgreementIdResData = {
	agreement_id: string;
};
export const callGenerateBillingAgreementIdApi = async (
	body: GenerateBillingAgreementIdReqData,
): Promise<ResponseParams<GenerateBillingAgreementIdResData>> => {
	const result: ResponseParams<GenerateBillingAgreementIdResData> = await requestHttpPost(
		`/paypal/billing-agreement-id`,
		body,
	);
	return result;
};

// 保存済みカード情報削除(特権)
export const callDeleteSavedCardApi = async (paymentTokenId: string): Promise<ResponseParams<void | ApiError>> => {
	const result: ResponseParams<void | ApiError> = await requestHttpDelete(`/paypal/payment-tokens/${paymentTokenId}`);
	return result;
};

// vault更新
export const callUpdateVaultApi = async (
	gidOrderNo: string,
	vault: boolean,
): Promise<ResponseParams<void | ApiError>> => {
	const result: ResponseParams<void | ApiError> = await requestHttpPut(`/order/${gidOrderNo}/vault`, {
		vault: Number(vault),
	});
	return result;
};

// 注文情報認証（一括）
export type GetBulkOrderResData = Omit<GetOrderResData, 'net_amount' | 'discount' | 'postage' | 'tax' | 'remarks'> & {
	capture_detail: PriceDetail[]; // 決済内訳
	items: Item[]; // 商品情報
	periodic_info: PeriodicInfo; // 定期購読専用情報
	immediate_capture: boolean; // 即時決済かどうか
	user_detail?: StringDetail[]; // 注文者情報
	remarks?: StringDetail[]; // 支払情報備考
	shipping_method?: StringDetail[]; // 配送方法
	various_links?: VariousLink[]; // 各種リンク
};
type NumberDetail = {
	key: string; // 表示名
	value: number; // 値
};
type PriceDetail = NumberDetail & {
	is_price: boolean;
};
export type StringDetail = {
	key: string; // 表示名
	value: string; // 値
};
export type Item = {
	id?: string; // 商品ID
	title: string; // タイトル
	application_period?: string; // 申込期間
	image_url?: string; // 商品画像URL
	detail: PriceDetail[]; // 内訳
	subtotal: NumberDetail; // 小計
};
type PeriodicInfo = {
	free_text?: string; // フリーテキスト
	payments: Payment[]; // 支払情報
};
type Payment = {
	title: string; // タイトル
	total: number; // 合計金額
	detail: PriceDetail[]; // 内訳
	sub_text?: string; // サブテキスト
};
type VariousLink = {
	key: string;
	url: string;
};
export const callGetBulkOrderApi = async (gidOrderToken: string): Promise<ResponseParams<GetBulkOrderResData>> => {
	const result: ResponseParams<GetBulkOrderResData> = await requestHttpGet(`/order/bulk/token/${gidOrderToken}`);
	return result;
};

// 決済情報作成（一括）
export type CreateBulkOrderReqData = {
	gid_order_no: string; // 注文ID
	payment_tokens_id?: string; // 保存済みカード情報ID
	agreement_id?: string;
	paypal_client_metadata_id?: string; // FraudNetのFパラメータ
	payment_type: number; // 支払区分 1:PayPal, 2:クレジットカード
};
export type CreateBulkOrderResData = {
	paypal_order_id: string; // 決済ID
};
export const callCreateBulkOrderApi = async (
	body: CreateBulkOrderReqData,
): Promise<ResponseParams<CreateBulkOrderResData>> => {
	const result: ResponseParams<CreateBulkOrderResData> = await requestHttpPost(`/order/bulk/checkout`, body);
	return result;
};

type ConfirmBulkOrderReqData = {
	payment_type: number; // 支払い方法 (1:PayPal,2:クレジット,3:後払い)
};
type ConfirmBulkOrderResData = {
	order_status: string; // PayPal注文のステータス
	authorization_status: string; // PayPalオーソリのステータス
};
// 注文情報確定（一括）(オーソリ実行)
export const callConfirmBulkOrderApi = async (
	gidOrderNo: string,
	body: ConfirmBulkOrderReqData,
): Promise<ResponseParams<ConfirmBulkOrderResData | ApiError>> => {
	const result: ResponseParams<ConfirmBulkOrderResData | ApiError> = await requestHttpPut(
		`/order/bulk/${gidOrderNo}/confirm`,
		body,
	);
	return result;
};

// オーソリ決済
type CaptureOrderResData = {
	authorization_status: string;
	capture_status: string;
};
export const callCaptureOrderApi = async (gidOrderNo: string): Promise<ResponseParams<CaptureOrderResData>> => {
	const result: ResponseParams<CaptureOrderResData> = await requestHttpPut(`/authorizations/${gidOrderNo}/capture`, {});
	return result;
};
